/**
 * Utility functions for error handling and validation
 */

const AXIOS_TRANSPORT_MESSAGE = /^Request failed with status code \d+$/i

const GENERIC_HTTP_STATUS_FALLBACKS: Record<number, string> = {
  400: 'The request was invalid. Please check your input and try again.',
  401: 'Your session has expired. Please sign in again.',
  403: 'You do not have permission to perform this action.',
  404: 'The requested resource was not found.',
  409: 'This value is already in use. Please try again with a different value.',
  422: 'Some details are invalid. Please review and try again.',
  500: 'Something went wrong on the server. Please try again later.'
}

/** OMS product screen–specific message mapping (used via formatUserFacingApiError only). */
const OMS_API_MESSAGE_OVERRIDES: Record<string, string> = {
  'Duplicate external ID':
    'This Product ID is already in use. Please enter a different Product ID.',
  'Duplicate POS PLU':
    'This POS Plu is already in use. Please enter a different POS Plu.',
  'Product already exists':
    'This POS Plu is already assigned to another product. Please use a different POS Plu.',
  'Product not found': 'The selected product could not be found. It may have been removed.',
  'No fields provided for update': 'No changes were provided to save.',
  'Invalid payload': 'Some required product information is missing or invalid.'
}

function isAxiosTransportMessage(message: string): boolean {
  return AXIOS_TRANSPORT_MESSAGE.test(message.trim())
}

function mapOmsApiMessage(message: string): string {
  const trimmed = message.trim()
  if (!trimmed) {
    return trimmed
  }

  return OMS_API_MESSAGE_OVERRIDES[trimmed] ?? trimmed
}

/**
 * Normalizes an error (string, array, or object) to a displayable string for dialogs/toasts.
 *
 * Handles the common NestJS error shapes:
 *   - plain string
 *   - { statusCode, message: string, error?: string }   (e.g. BadRequestException)
 *   - { statusCode, message: string[], error?: string } (e.g. ValidationPipe)
 *   - axios-style { response: { data: ... } } — API body is preferred over axios transport text
 */
export const getErrorMessage = (error: unknown): string => {
  const fallback = 'An unexpected error occurred'

  if (error == null) {
    return fallback
  }
  if (typeof error === 'string') {
    const trimmed = error.trim()

    return trimmed || fallback
  }
  if (Array.isArray(error)) {
    const joined = error.filter(Boolean).map(String).join(', ')

    return joined || fallback
  }
  if (typeof error === 'object') {
    const e = error as {
      message?: unknown
      error?: unknown
      response?: { status?: number; data?: unknown }
    }

    if (e.response?.data) {
      const fromApi = getErrorMessage(e.response.data)
      if (fromApi !== fallback) {
        return fromApi
      }
    }

    if (Array.isArray(e.message)) {
      const joined = (e.message as unknown[]).filter(Boolean).map(String).join(', ')
      if (joined) {
        return joined
      }
    }
    if (typeof e.message === 'string' && e.message.trim()) {
      const msg = e.message.trim()
      if (!isAxiosTransportMessage(msg)) {
        return msg
      }
    }
    if (typeof e.error === 'string' && e.error.trim()) {
      const err = e.error.trim()
      if (err !== 'Conflict' && err !== 'Internal Server Error') {
        return err
      }
    }

    const status = e.response?.status
    if (status && GENERIC_HTTP_STATUS_FALLBACKS[status]) {
      return GENERIC_HTTP_STATUS_FALLBACKS[status]
    }
  }

  return fallback
}

/**
 * User-facing API error text for OMS product screens (forms and snackbars).
 */
export const formatUserFacingApiError = (
  error: unknown,
  fallback = 'An unexpected error occurred. Please try again.'
): string => {
  const message = mapOmsApiMessage(getErrorMessage(error))

  return message === 'An unexpected error occurred' ? fallback : message
}

/**
 * Checks if an error payload represents an unauthorized (403) or forbidden (401) error.
 * This function handles various error formats that may come from different API clients or error handlers.
 *
 * @param errorPayload - The error object or string to check. Can be:
 *   - An error object with statusCode, status, error, response, or message properties
 *   - A string error message
 *   - null or undefined
 *
 * @returns true if the error is a 403 Forbidden or 401 Unauthorized error, false otherwise
 *
 * @example
 * ```typescript
 * const error = { statusCode: 403, message: 'Access denied' }
 * if (isUnauthorizedError(error)) {
 *   router.replace(ROUTES.UNAUTHORIZED)
 * }
 * ```
 */
export const isUnauthorizedError = (errorPayload: any): boolean => {
  if (!errorPayload) {
    return false
  }

  if (typeof errorPayload === 'string') {
    const lowerMessage = errorPayload.toLowerCase()

    return (
      lowerMessage.includes('forbidden') ||
      lowerMessage.includes('unauthorized') ||
      lowerMessage.includes('does not have access')
    )
  }

  if (typeof errorPayload === 'object' && errorPayload !== null) {
    if (errorPayload.statusCode === 403 || errorPayload.statusCode === 401) {
      return true
    }

    if (errorPayload.error === 'Forbidden' || errorPayload.error === 'Unauthorized') {
      return true
    }

    if (errorPayload.response?.status === 403 || errorPayload.response?.status === 401) {
      return true
    }

    if (errorPayload.status === 403 || errorPayload.status === 401) {
      return true
    }

    if (errorPayload.message) {
      const lowerMessage = String(errorPayload.message).toLowerCase()
      if (
        lowerMessage.includes('forbidden') ||
        lowerMessage.includes('unauthorized') ||
        lowerMessage.includes('does not have access')
      ) {
        return true
      }
    }
  }

  return false
}
